/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "semantic-ui-react";
import { TextField } from "@mui/material";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { selectEditMode } from "../../slices/editMode/editModeSlice";
import { selectAllResources, selectResourceById } from "../../slices/resourceCollection/resourceCollectionSlice";
import { selectTopSectionById, topSectionUpdated } from "../../slices/topSections/topSectionsSlice";
import generateResourceTextNarrative from "../compositionResourceFunctions/generateResourceTextNarrative";
import adjustEmptyReason from "../compositionResourceFunctions/adjustEmptyReason";
import jsonEquals from "../../functions/jsonEquals";
import NarrativeEntry from "../../datatypeComponents/NarrativeEntry";
import ReferenceEntry, { DisplayFromFHIRReference } from "../../datatypeComponents/ReferenceEntry";
import CodeableConceptEntry, { simpleDisplayStringFromFHIRCodeableConcept } from "../../datatypeComponents/CodeableConceptEntry";
import SectionEntryArrayEntry from "./SectionEntryArrayEntry";
import EditSectionArrayModal from "./EditSectionArrayModal";
import SectionDisplay from "./SectionDisplay";
import { emptyReasonCodeableConceptSet, emptyReasonNotStarted, emptyTextNoData, sectionCodeCodeableConceptSet } from "../../dictionaries/CodeSystemLookup";

const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const dayOfWeekLookup = { "Sunday": 0, "sunday": 0, "Monday": 1, "monday": 1, "Tuesday": 2, "tuesday": 2, "Wednesday": 3, "wednesday": 3, "Thursday": 4, "thursday": 4, "Friday": 5, "friday": 5, "Saturday": 6, "saturday": 6 };
//This isn't perfect, there is a small window where this will give an inaccurate converted time. If someone further east than UTC+2 is looking at Monday the week before the daylight savings time switch in ET.
// The most likely scenario for it being wrong would be someone in Asia looking at Monday the week before the daylight savings time switch for Eastern US.
const getConvertedFutureTime = (day, timeHours, timeMinutes = 0, rejectIfSameTime = false) => {
    let localNextOfThatDayOfWeek = new Date();
    localNextOfThatDayOfWeek.setDate(localNextOfThatDayOfWeek.getDate() + (dayOfWeekLookup[day] + 7 - localNextOfThatDayOfWeek.getDay()) % 7);
    localNextOfThatDayOfWeek.setHours(timeHours, timeMinutes, 0, 0);
    let newYorkTime = new Date(localNextOfThatDayOfWeek).toLocaleString("en-US", { timeZone: "America/New_York" });
    localNextOfThatDayOfWeek.setMilliseconds(localNextOfThatDayOfWeek - new Date(newYorkTime));
    if (rejectIfSameTime && localNextOfThatDayOfWeek.toString() === (new Date(newYorkTime)).toString()) {
        return false;
    }
    return localNextOfThatDayOfWeek;
}

const getTimeAndAmOrPM = (singleTime) => {
    let time;
    let pm = false;
    let error = false;
    let beforeLastTwoCharacters = singleTime.substring(0, singleTime.length - 2).trim();
    if (beforeLastTwoCharacters !== "" && !isNaN(beforeLastTwoCharacters)) {
        let lastTwoCharacters = singleTime.substring(singleTime.length - 2);
        time = beforeLastTwoCharacters;
        if (lastTwoCharacters === "am") {
            //It's am
        } else if (lastTwoCharacters === "pm") {
            pm = true;
        } else {
            error = true;
            console.log("Error, couldn't find am or pm");
        }
    }
    if (time) {
        time = parseInt(time);
    }
    return [time, pm, error];
}

const processTimeTableRow = (row, longTimeZone = "", shortTimeZone = "") => {
    let error = false;
    let differentDay = "";
    let cells = row.replace("<tr>", "").replace("</tr>", "").split("<td");
    cells = cells.slice(1);
    let originalTimeCell = cells[0];
    console.log(originalTimeCell);
    originalTimeCell = (originalTimeCell.substring(originalTimeCell.indexOf(">") + 1, originalTimeCell.indexOf("</td"))).replaceAll("\n", " ").replaceAll(" ", " ").replaceAll("&#160;", " ").replaceAll("  ", " ").trim().toLowerCase();
    let dayOfWeek = originalTimeCell.split(" ")[0];
    let convertedTimeString = "";
    if (dayOfWeekLookup[dayOfWeek]) {
        let endTime;
        let startTime;
        let pm = false; //It's an AM time unless shown otherwise
        let restOfCell = originalTimeCell.substring(dayOfWeek.length + 1).replaceAll(" - ", "-").replaceAll(" -", "-").replaceAll("- ", "-").trim();
        let hyphenIndex = restOfCell.indexOf("-");
        if (hyphenIndex !== -1 && hyphenIndex !== 0) {
            let firstHalf = restOfCell.substring(0, hyphenIndex).replaceAll(".", "").trim();
            let secondHalf = restOfCell.substring(hyphenIndex + 1).replaceAll(".", "").trim();
            if (firstHalf !== "" && !isNaN(firstHalf)) {
                startTime = parseInt(firstHalf);
                //Need to get whether it's am or pm by looking at end time
                [endTime, pm, error] = getTimeAndAmOrPM(secondHalf);
                if (endTime && startTime > endTime && pm) {
                    //This may indicate that something is wrong, otherwise this is a VERY VERY long meeting
                    pm = false;	//So assuming that the first time is am
                }
            } else {
                //Possibly has the am or pm in the first half
                [startTime, pm, error] = getTimeAndAmOrPM(firstHalf);
            }
        } else {
            //assumes that this is just a start time and no end time specified
            [startTime, pm, error] = getTimeAndAmOrPM(restOfCell);
        }

        if (error === false && startTime) {
            if (pm === false && startTime === 12) {
                //Then it's midnight
                //startTime = 0;
                console.log("Warning, the start time is midnight. Defaulting to noon time, if you don't want this please uncomment code above.");
            } else if (pm && startTime !== 12) {
                startTime += 12;    //If it's pm and not noon, then add 12 to the time
            }
            let localTime = getConvertedFutureTime(dayOfWeek, startTime, 0, true);
            if (localTime) {
                convertedTimeString = localTime.toLocaleTimeString(navigator.language, { hour: '2-digit', minute: '2-digit' }); //It will show something like 3:00pm or 15:00 depending on the language settings

                //Checks if day of the week is different, for example 8pm ET on Monday is going to be 2am Prague Time on Tuesday
                if (localTime.getDay() !== dayOfWeekLookup[dayOfWeek]) {
                    differentDay = days[localTime.getDay()] + " ";
                }
            }
        }
    } else {
        error = true;
        console.log("Couldn't find day of week: " + dayOfWeek);
    }
    let localTimeCell = "<td>" + differentDay + convertedTimeString.toLowerCase() + " [" + shortTimeZone + "]</td>";
    let newRow = "<tr>";
    for (let i = 0; i < cells.length; i++) {
        let cell = "<td" + cells[i];
        newRow += cell;
        if (i === 0) {
            newRow += localTimeCell
        }
    }
    newRow += "</tr>";
    if (error) {
        return false;
    }
    return newRow;
}

const convertTimeTable = (detail) => {
    let localTime = getConvertedFutureTime("Tuesday", 19, 0, true); //Just to check if different time zone
    if (localTime) {
        let timeTable = detail;
        let longTimeZone = localTime.toLocaleDateString(undefined, { day: '2-digit', timeZoneName: 'long' }).substring(4); //Will say something like Central European Summer Time
        let shortTimeZone = localTime.toLocaleDateString(undefined, { day: '2-digit', timeZoneName: 'short' }).substring(4); //Will say something like GMT+2 or CDT
        let timeZone = longTimeZone || shortTimeZone;
        timeZone = timeZone.replace(" Time", "");
        timeTable = timeTable.replace("<th>Day/Time (Eastern US)</th>", "<th>Day/Time (Eastern US)</th><th>(" + timeZone + ")</th>");

        let tbodyStart = timeTable.indexOf("<tbody>") + 7;
        let tbodyEnd = timeTable.indexOf("</tbody>");
        let tbody = timeTable.substring(tbodyStart, tbodyEnd);

        let rows = tbody.split("<tr");
        rows = rows.slice(1);
        let changedTbody = "";
        for (let i = 0; i < rows.length; i++) {
            let row = "<tr" + rows[i];
            row = processTimeTableRow(row, longTimeZone, shortTimeZone);
            if (row) {
                changedTbody += row;
            } else {
                return false;
            }
        }
        timeTable = timeTable.substring(0, tbodyStart) + changedTbody + timeTable.substring(tbodyEnd);

        return timeTable;
    }
    return false;
}

const TopSectionSliceDisplay = ({ foi, setCompositionResourceState, sectionIndex, resourceReferenceMap,
    sectionNavigationElementIds, associatedOutcomeMeasureReportState, isM11ReportProfile,
    containedResourceDictionary }) => {

    const topSection = useSelector(state => selectTopSectionById(state, sectionIndex));
    //entity: {  
    //     id: sectionIndex,
    //     displayTitle: section.title || section.code..display,
    //     compositionId: parent FOI,
    //     productFamily: ProfileName,
    //     initialSection: sectionJson,
    //     revisedSection: sectionJson
    // 
    //     sectionJson = { extension, title, code, author, focus, text, orderedBy, entry, emptyReason, section }
    const topSectionJson = topSection?.revisedSection;
    const compositionType = topSection?.productFamily || "Composition";
    const compositionTitle = resourceReferenceMap?.compositionTitle;
    const editMode = useSelector(state => selectEditMode(state));
    const dispatch = useDispatch();
    const resourceCollection = useSelector(state => selectAllResources(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));

    const [sectionState, setSectionState] = useState({});
    const [generatedNarrativeState, setGeneratedNarrativeState] = useState("");
    const [editSectionModal, setEditSectionModal] = useState({ "openModal": false });
    const [headerDisplayState, setHeaderDisplayState] = useState("");
    const [sectionLoaded, setSectionLoaded] = useState({});
    const [noneditableNetEffectSectionState, setNoneditableNetEffectSectionState] = useState(false);

    useEffect(() => {
        if (topSectionJson && Object.keys(topSectionJson).length > 0) {
            if (sectionIndex !== topSection.id) {
                alert("sectionIndex " + sectionIndex + " but topSection.id is " + topSection.id);
            }
            let startingSectionStateValues = {
                "sectionIndex": topSection.id,
                "editTitle": false,
                "extension": topSectionJson.extension ? JSON.parse(JSON.stringify(topSectionJson.extension)) : [],
                "title": topSectionJson.title || "",
                "code": topSectionJson.code ? JSON.parse(JSON.stringify(topSectionJson.code)) : null,
                "author": topSectionJson.author ? JSON.parse(JSON.stringify(topSectionJson.author)) : [],
                "focus": topSectionJson.focus ? JSON.parse(JSON.stringify(topSectionJson.focus)) : null,
                "text": topSectionJson.text ? JSON.parse(JSON.stringify(topSectionJson.text)) : {
                    "status": "empty",
                    "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[No narrative text.]</p></div>'
                },
                "orderedBy": topSectionJson.orderedBy ? JSON.parse(JSON.stringify(topSectionJson.orderedBy)) : null,
                "entry": topSectionJson.entry ? JSON.parse(JSON.stringify(topSectionJson.entry)) : [],
                "emptyReason": topSectionJson.emptyReason ? JSON.parse(JSON.stringify(topSectionJson.emptyReason)) : null,
                "section": topSectionJson?.section ? JSON.parse(JSON.stringify(topSectionJson.section)) : []
            };

            let autoChangedEmptyReasonChanged = "no change";
            let sectionIsEmpty = true;
            if (startingSectionStateValues.section?.length) {
                sectionIsEmpty = false;
            } else if (startingSectionStateValues.entry?.length) {
                sectionIsEmpty = false;
            } else if (startingSectionStateValues.text?.status && startingSectionStateValues.text.status !== "empty") {
                sectionIsEmpty = false;
            }
            if (sectionIsEmpty && !startingSectionStateValues.emptyReason) {
                autoChangedEmptyReasonChanged = JSON.parse(JSON.stringify(emptyReasonNotStarted));
                startingSectionStateValues.emptyReason = autoChangedEmptyReasonChanged;
            }
            if (!sectionIsEmpty && startingSectionStateValues.emptyReason) {
                autoChangedEmptyReasonChanged = null;
                startingSectionStateValues.emptyReason = null;
            }
            setSectionState(startingSectionStateValues);
            if (editMode && autoChangedEmptyReasonChanged !== "no change" && autoChangedEmptyReasonChanged !== undefined
                && (topSection.initialSection?.emptyReason || autoChangedEmptyReasonChanged)
                && !jsonEquals(topSection.revisedSection?.emptyReason || null, autoChangedEmptyReasonChanged)) {
                console.log("resourceUpdate for Composition.section[" + sectionIndex + "].emptyReason");
                let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
                newTopSectionDetail.revisedSection.emptyReason = autoChangedEmptyReasonChanged;
                dispatch(topSectionUpdated(newTopSectionDetail));
                setCompositionResourceState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionIndex].emptyReason = autoChangedEmptyReasonChanged;
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
            }
            let generatedNarrative;
            if (topSectionJson?.entry?.length === 1 && topSectionJson.entry[0]?.reference?.split("/").length === 2 &&
                !isNaN(topSectionJson.entry[0].reference.split("/")[1])) {
                let resourceFoi = topSectionJson.entry[0].reference.split("/")[1];
                let resourceDictionary = {};
                for (const entry of resourceCollection) {
                    resourceDictionary[entry.id] = entry;
                }
                if (resourceDictionary[resourceFoi] && resourceDictionary[resourceFoi].resourceJson) {
                    let theResourceJson = resourceDictionary[resourceFoi].resourceJson;
                    generatedNarrative = theResourceJson ? JSON.parse(JSON.stringify(generateResourceTextNarrative(theResourceJson))) : theResourceJson;
                }
            }
            if (generatedNarrative) {
                setGeneratedNarrativeState(generatedNarrative);
            }
            let headerDisplay = topSectionJson.title;
            if (!headerDisplay) {
                if (topSectionJson.code) {
                    headerDisplay = simpleDisplayStringFromFHIRCodeableConcept(topSectionJson.code) + " section";
                } else {
                    headerDisplay = "Untitled Section";
                }
            }
            setHeaderDisplayState(headerDisplay);
            let sectionCode = topSectionJson?.code?.coding?.[0]?.code || topSectionJson?.code?.text;
            let sectionPointer = topSectionJson?.focus?.reference ? JSON.parse(JSON.stringify(topSectionJson.focus.reference)) : sectionCode;
            setSectionLoaded({
                "section": JSON.parse(JSON.stringify(topSectionJson)), "sectionCode": sectionCode, "sectionPointer": sectionPointer,
                "compositionType": compositionType, "compositionTitle": compositionTitle,
                "generatedNarrative": generatedNarrative ? JSON.parse(JSON.stringify(generatedNarrative)) : generatedNarrative,
                "focus": topSectionJson?.focus ? JSON.parse(JSON.stringify(topSectionJson.focus)) : topSectionJson.focus
            });
            if (compositionType === "SummaryOfNetEffect" &&
                (sectionCode === "column-headers" || sectionCode === "summary-of-findings" ||
                    sectionCode === "net-effect-estimate")
            ) {
                setNoneditableNetEffectSectionState(true);
            }
        }
    }, [topSectionJson, editMode]);

    useEffect(() => {
        if (editMode && topSection?.compositionId && sectionState.editTitle === false) {
            if (sectionState.title) {
                setHeaderDisplayState(sectionState.title);
            }
            if (sectionState.title !== undefined && !jsonEquals(topSection.revisedSection?.title || null, sectionState.title)) {
                console.log("resourceUpdate for Composition.section[" + sectionIndex + "].title");
                let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
                newTopSectionDetail.revisedSection.title = sectionState.title;
                dispatch(topSectionUpdated(newTopSectionDetail));
                setCompositionResourceState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionIndex].title = sectionState.title;
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
            }
        }
    }, [sectionState.title, sectionState.editTitle]);

    useEffect(() => {
        if (editMode && topSection?.compositionId && sectionState.code !== undefined && !jsonEquals(topSection.revisedSection?.code || null, sectionState.code)) {
            console.log("resourceUpdate for Composition.section[" + sectionIndex + "].code");
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            newTopSectionDetail.revisedSection.code = sectionState.code ? JSON.parse(JSON.stringify(sectionState.code)) : sectionState.code;
            dispatch(topSectionUpdated(newTopSectionDetail));
            setCompositionResourceState(prevState => {
                let newSection = JSON.parse(JSON.stringify(prevState.section));
                newSection[sectionIndex].code = sectionState.code;
                return {
                    ...prevState,
                    section: newSection
                }
            });
        }
    }, [sectionState.code]);

    useEffect(() => {
        if (editMode && topSection?.compositionId && sectionState.focus !== undefined
            && !jsonEquals(topSection.revisedSection?.focus || null, sectionState.focus)) {
            console.log("resourceUpdate for Composition.section[" + sectionIndex + "].focus");
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            newTopSectionDetail.revisedSection.focus = sectionState.focus ? JSON.parse(JSON.stringify(sectionState.focus)) : sectionState.focus;
            dispatch(topSectionUpdated(newTopSectionDetail));
            setCompositionResourceState(prevState => {
                let newSection = JSON.parse(JSON.stringify(prevState.section));
                newSection[sectionIndex].focus = sectionState.focus ? JSON.parse(JSON.stringify(sectionState.focus)) : sectionState.focus;
                return {
                    ...prevState,
                    section: newSection
                }
            });
        }
    }, [sectionState.focus]);

    useEffect(() => {
        if (editMode && topSection?.compositionId && sectionState.text !== undefined
            && (topSection.initialSection?.text || !jsonEquals(sectionState.text, emptyTextNoData))
            && !jsonEquals(topSection.revisedSection?.text || null, sectionState.text)) {
            console.log("resourceUpdate for Composition.section[" + sectionIndex + "].text");
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            newTopSectionDetail.revisedSection.text = sectionState.text ? JSON.parse(JSON.stringify(sectionState.text)) : sectionState.text;
            let autoChangedEmptyReasonChanged = adjustEmptyReason(sectionState, setSectionState);
            if (autoChangedEmptyReasonChanged !== "no change") {
                let previousInitialValueForEmptyReason = newTopSectionDetail.initialSection?.emptyReason || null;
                newTopSectionDetail.revisedSection.emptyReason = autoChangedEmptyReasonChanged ? JSON.parse(JSON.stringify(autoChangedEmptyReasonChanged)) : autoChangedEmptyReasonChanged;
                if (autoChangedEmptyReasonChanged !== undefined && !jsonEquals(newTopSectionDetail.revisedSection?.emptyReason || null, autoChangedEmptyReasonChanged) &&
                    !(!previousInitialValueForEmptyReason && !autoChangedEmptyReasonChanged)) {
                    console.log("resourceUpdate for Composition.section[" + sectionIndex + "].emptyReason");
                } else {
                    autoChangedEmptyReasonChanged = "no change";
                }
            }
            dispatch(topSectionUpdated(newTopSectionDetail));
            setCompositionResourceState(prevState => {
                let newSection = JSON.parse(JSON.stringify(prevState.section));
                newSection[sectionIndex].text = typeof (sectionState.text) === "object" ? JSON.parse(JSON.stringify(sectionState.text)) : sectionState.text;
                if (autoChangedEmptyReasonChanged !== "no change") {
                    newSection[sectionIndex].emptyReason = typeof (autoChangedEmptyReasonChanged) === "object" ? JSON.parse(JSON.stringify(autoChangedEmptyReasonChanged)) : autoChangedEmptyReasonChanged;
                }
                return {
                    ...prevState,
                    section: newSection
                }
            });
        }
    }, [sectionState.text]);

    useEffect(() => {
        //console.log("TopSectionSliceDisplay useEffect due to change in sectionState.entry")
        if (editMode && topSection?.compositionId && sectionState.entry !== undefined
            && (topSection.initialSection?.entry || !Array.isArray(sectionState.entry) || sectionState.entry[0] || sectionState.entry.length > 1)
            && !jsonEquals(topSection.revisedSection?.entry || null, sectionState.entry)
        ) {
            console.log("resourceUpdate for Composition.section[" + sectionIndex + "].entry");
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            newTopSectionDetail.revisedSection.entry = sectionState.entry;
            let autoChangedEmptyReasonChanged = adjustEmptyReason(sectionState, setSectionState);
            if (autoChangedEmptyReasonChanged !== "no change") {
                let previousInitialValueForEmptyReason = newTopSectionDetail.initialSection?.emptyReason || null;
                newTopSectionDetail.revisedSection.emptyReason = autoChangedEmptyReasonChanged;
                if (autoChangedEmptyReasonChanged !== undefined &&
                    (autoChangedEmptyReasonChanged || previousInitialValueForEmptyReason) &&
                    !jsonEquals(newTopSectionDetail.revisedSection?.emptyReason || null, autoChangedEmptyReasonChanged)) {
                    console.log("resourceUpdate for Composition.section[" + sectionIndex + "].emptyReason");
                } else {
                    autoChangedEmptyReasonChanged = "no change";
                }
            }
            dispatch(topSectionUpdated(newTopSectionDetail));
            setCompositionResourceState(prevState => {
                let newSection = JSON.parse(JSON.stringify(prevState.section));
                newSection[sectionIndex].entry = sectionState.entry;
                if (autoChangedEmptyReasonChanged !== "no change") {
                    newSection[sectionIndex].emptyReason = autoChangedEmptyReasonChanged;
                }
                return {
                    ...prevState,
                    section: newSection
                }
            });
        }
    }, [sectionState.entry]);

    useEffect(() => {
        if (editMode && topSection?.compositionId && sectionState.emptyReason !== undefined) {
            const emptyReason = topSection.revisedSection?.emptyReason;
            if ((emptyReason || sectionState.emptyReason) && !jsonEquals(emptyReason, sectionState.emptyReason)) {
                console.log("resourceUpdate for Composition.section[" + sectionIndex + "].emptyReason");
                let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
                newTopSectionDetail.revisedSection.emptyReason = sectionState.emptyReason;
                dispatch(topSectionUpdated(newTopSectionDetail));
                setCompositionResourceState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionIndex].emptyReason = sectionState.emptyReason;
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
            }
        }
    }, [sectionState.emptyReason]);

    useEffect(() => {
        if (editMode && topSection?.compositionId && sectionState.section !== undefined &&
            (topSection.initialSection?.section || !Array.isArray(sectionState.section) || sectionState.section.length > 1 || sectionState.section[0]) &&
            !jsonEquals(topSection.revisedSection?.section || null, sectionState.section)) {
            console.log("resourceUpdate for Composition.section[" + sectionIndex + "].section");
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            newTopSectionDetail.revisedSection.section = sectionState.section;
            let autoChangedEmptyReasonChanged = adjustEmptyReason(sectionState, setSectionState);
            if (autoChangedEmptyReasonChanged !== "no change") {
                let previousInitialValueForEmptyReason = newTopSectionDetail.initialSection?.emptyReason || null;
                newTopSectionDetail.revisedSection.emptyReason = autoChangedEmptyReasonChanged;
                if (autoChangedEmptyReasonChanged !== undefined &&
                    (previousInitialValueForEmptyReason || autoChangedEmptyReasonChanged) &&
                    !jsonEquals(newTopSectionDetail.revisedSection?.emptyReason || null, autoChangedEmptyReasonChanged)) {
                    console.log("resourceUpdate for Composition.section[" + sectionIndex + "].emptyReason");
                } else {
                    autoChangedEmptyReasonChanged = "no change";
                }
            }
            dispatch(topSectionUpdated(newTopSectionDetail));
            setCompositionResourceState(prevState => {
                let newSection = JSON.parse(JSON.stringify(prevState.section));
                newSection[sectionIndex].section = sectionState.section;
                if (autoChangedEmptyReasonChanged !== "no change") {
                    newSection[sectionIndex].emptyReason = autoChangedEmptyReasonChanged;
                }
                return {
                    ...prevState,
                    section: newSection
                }
            });
        }
    }, [sectionState.section]);

    let timeTable;
    if (sectionState?.text?.div && sectionState.text.div.indexOf("<thead><tr><th>Day/Time (Eastern US)</th>") !== -1) {
        timeTable = convertTimeTable(sectionState.text.div);
    }

    return <>
        {(sectionState &&
            (sectionState.title || sectionState.text || sectionState.entry || sectionState.section || sectionState.emptyReason)
        ) &&
            <div id={isM11ReportProfile ? sectionNavigationElementIds[sectionIndex + 1] : sectionNavigationElementIds[sectionIndex]}>
                {(editMode && !noneditableNetEffectSectionState) ?
                    <>
                        {sectionState.editTitle ?
                            <>
                                <div style={{ margin: "8px 0px 0px 0px" }}><b>Press Enter key when done editing the section title.</b></div>
                                <TextField style={{ width: "100%" }} className="inputField"
                                    type='text' variant='outlined'
                                    placeholder='Enter section title.'
                                    value={sectionState.title}
                                    onKeyUp={(event) => {
                                        if (event.key === 'Enter') {
                                            setSectionState(prevState => {
                                                return {
                                                    ...prevState,
                                                    "editTitle": false
                                                }
                                            });
                                        }
                                    }}
                                    onChange={(e) => {
                                        setSectionState(prevState => {
                                            return {
                                                ...prevState,
                                                "title": e.target.value || ""
                                            }
                                        });
                                    }} />
                            </>
                            :
                            <h3>
                                <span>{sectionState.title || "Untitled Section (" + headerDisplayState + ")"}</span>
                                <span>
                                    <button type="button" style={{ marginLeft: "12px" }}
                                        onClick={() => {
                                            setSectionState(prevState => {
                                                return {
                                                    ...prevState,
                                                    "editTitle": true
                                                }
                                            });
                                        }} >✎ Edit title</button>
                                </span>
                            </h3>
                        }</>
                    :
                    <h3>{headerDisplayState}{(noneditableNetEffectSectionState === true && editMode) &&
                        " -- this is not fully and reliably editable here. Use the Summary Table or the JSON View."}</h3>
                }
                <div style={{ marginLeft: "24px" }}>
                    <div>
                        {(editMode && !noneditableNetEffectSectionState) ?
                            <NarrativeEntry elementName="text"
                                startingValue={sectionState.text}
                                generatedNarrative={generatedNarrativeState}
                                calledFrom={isM11ReportProfile ? "M11Report" : "TopSectionSliceDisplay"}
                                setResourceState={setSectionState} />
                            :
                            <>{(sectionState.text?.div &&
                                sectionState.text.status !== "empty") &&
                                <>
                                    {timeTable ?
                                        <ReactMarkdown children={timeTable} rehypePlugins={[rehypeRaw]} />
                                        :
                                        <ReactMarkdown children={sectionState?.text?.div}
                                            rehypePlugins={[rehypeRaw]} />
                                    }
                                    <br />
                                </>}</>
                        }
                    </div>
                    {(editMode && !noneditableNetEffectSectionState) &&
                        <CodeableConceptEntry elementName="code" fieldLabel="Section Code"
                            startingValue={sectionState.code}
                            codeableConceptLevelValueSet={sectionCodeCodeableConceptSet}
                            setResourceState={setSectionState} startCollapsed />
                    }
                    {
                        (editMode && !noneditableNetEffectSectionState) ?
                            <>
                                <ReferenceEntry elementName="focus" fieldLabel="Section Focus"
                                    containedResourceDictionary={containedResourceDictionary}
                                    startingValue={sectionState.focus} enableCreation={true}
                                    setResourceState={setSectionState} deletable startCollapsed />
                            </>
                            :
                            <>
                                {sectionState.focus && <div>
                                    <div>
                                        <b>Section focus: </b>
                                        <DisplayFromFHIRReference reference={sectionState.focus}
                                            containedResourceJson={sectionState.focus.reference && containedResourceDictionary[sectionState.focus.reference]} />
                                    </div>
                                    <p></p>
                                </div>}
                            </>
                    }
                    {(sectionState.entry?.length > 0 || sectionState.section?.length > 0 || editMode) &&
                        <>
                            <p></p>
                            <p style={{ marginBottom: "0px" }}><b>This section contains:</b></p>
                        </>
                    }
                    <div style={{ marginLeft: "24px" }}>
                        {(editMode && !noneditableNetEffectSectionState) ?
                            <>
                                {sectionLoaded?.section &&
                                    <SectionEntryArrayEntry sectionState={sectionState}
                                        setSectionState={setSectionState}
                                        sectionLoaded={sectionLoaded}
                                        foi={foi} slicePattern={true}
                                        revisedResourceJson={parentResourceDetail?.revisedResourceJson}
                                        resourceReferenceMap={resourceReferenceMap}
                                        associatedOutcomeMeasureReportState={associatedOutcomeMeasureReportState} />
                                }
                            </>
                            :
                            <>
                                {(sectionState.entry?.length > 0) &&
                                    <>
                                        {sectionState.entry.map((entry, entryIndex) => {
                                            return <span key={entryIndex}>
                                                <p>
                                                    <DisplayFromFHIRReference reference={entry}
                                                        containedResourceJson={entry.reference && containedResourceDictionary[entry.reference]} />
                                                </p>
                                            </span>
                                        })}
                                    </>}
                            </>
                        }
                        {(editMode && !noneditableNetEffectSectionState) && <div>
                            <Button className="formButton" style={{ margin: "0px" /*color: "#000000", */ }}
                                content="Add/Delete/Reorganize Sections" compact
                                onClick={() => { setEditSectionModal({ "openModal": true }) }} />
                            <br />
                        </div>}
                        {editSectionModal.openModal && <EditSectionArrayModal editSectionModal={editSectionModal}
                            setEditSectionModal={setEditSectionModal}
                            sectionState={sectionState}
                            setSectionState={setSectionState} />}
                        {(sectionState.section?.length > 0) &&
                            sectionState.section.map((subsection, subsectionIndex) => {
                                return <div key={subsectionIndex}>
                                    <SectionDisplay sectionPath={[sectionIndex, subsectionIndex]}
                                        containedResourceDictionary={containedResourceDictionary}
                                        foi={foi} resourceReferenceMap={resourceReferenceMap}
                                        isM11ReportProfile={isM11ReportProfile}
                                        setParentSectionState={setSectionState} />
                                </div>
                            })}
                        {(editMode && !noneditableNetEffectSectionState) ?
                            <>
                                <CodeableConceptEntry elementName="emptyReason" fieldLabel="Empty Reason"
                                    startingValue={sectionState.emptyReason}
                                    codeableConceptLevelValueSet={emptyReasonCodeableConceptSet}
                                    setResourceState={setSectionState} deletable startCollapsed />
                            </>
                            :
                            <>{sectionState.emptyReason && <div>
                                <p>Section is empty:
                                    <span>{sectionState.emptyReason.coding?.[0]?.display} {sectionState.emptyReason.text}</span>
                                </p>
                            </div>}</>}
                    </div>
                </div>
            </div>}
    </>
}

export default TopSectionSliceDisplay;